<template>
  <div class="completed-hikes-app">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="display-4 m-0"><strong>Completed Hikes</strong></h1>
    </div>

    <hr class="my-3">

    <!-- Loading State -->
    <div v-if="loading && completedHikes.length === 0" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && completedHikes.length === 0" class="no-completed-hikes">
      <span class="material-icons">check_circle</span>
      <p class="text-muted mb-2">You haven't completed any hikes yet.</p>
      <p class="text-muted"><small>Mark hikes as completed from the hike details page.</small></p>
    </div>

    <!-- Completed Hikes List -->
    <div v-else class="completed-hikes-list">
      <CompletedHikeItem
        v-for="completedHike in completedHikes"
        :key="completedHike.id"
        :completed-hike="completedHike"
        @show-flyover="showFlyover"
        @export-gpx="exportGpx"
        @copy-share="triggerCopyModel"
        @add-to-list="launchAddToList"
        @request-download="handleDownloadRequest"
      />

      <!-- Load More -->
      <div v-if="hasNextPage" class="see-more d-flex justify-content-center m-0">
        <button class="btn btn-link" @click="loadMore" :disabled="loading">
          {{ loading ? 'Loading...' : 'See More' }}
        </button>
      </div>

      <!-- Total Count -->
      <div v-if="totalCount > 0" class="mt-4 text-center">
        <p class="text-muted">
          <small>You've completed {{ totalCount }} {{ totalCount === 1 ? 'hike' : 'hikes' }}! 🎉</small>
        </p>
      </div>
    </div>

    <CopyLinkModal ref="shareModal" helper-text="Copy the link to share this hike" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import CompletedHikeItem from './components/CompletedHikeItem.vue'
import CopyLinkModal from '../../utils/shared_components/CopyLinkModal.vue'
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal'

const completedHikes = ref([])
const loading = ref(false)
const currentPage = ref(1)
const totalCount = ref(0)
const hasNextPage = ref(false)
const shareModal = ref(null)

const QUERY = `
  query UserCompletedHikes($page: Int!, $perPage: Int!) {
    userCompletedHikes(page: $page, perPage: $perPage) {
      completedHikes {
        id
        completedAt
        notes
        hike {
          id
          name
          slug
          obfuscatedSlug
          difficulty
          distanceMeters
          elevationGainMeters
          publiclyVisible
          shareableUrl
          processingStatus
          mainCountry {
            slug
            name
          }
          mainRegion {
            slug
            name
          }
          staticMapImageUrl
          squareStaticMapImageUrl
        }
      }
      totalCount
      currentPage
      hasNextPage
    }
  }
`

async function fetchCompletedHikes(page = 1) {
  loading.value = true
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || ''
      },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          page,
          perPage: 20
        }
      })
    })

    const result = await response.json()

    if (result.data?.userCompletedHikes) {
      const data = result.data.userCompletedHikes

      if (page === 1) {
        completedHikes.value = data.completedHikes || []
      } else {
        completedHikes.value.push(...(data.completedHikes || []))
      }

      totalCount.value = data.totalCount || 0
      currentPage.value = data.currentPage || 1
      hasNextPage.value = data.hasNextPage || false
    }
  } catch (error) {
    console.error('Error fetching completed hikes:', error)
  } finally {
    loading.value = false
  }
}

function loadMore() {
  if (!loading.value && hasNextPage.value) {
    fetchCompletedHikes(currentPage.value + 1)
  }
}

function showFlyover(event, completedHike) {
  if (event && event.preventDefault) event.preventDefault()
  if (window.showMapModalForTrail && completedHike.hike?.id) {
    window.showMapModalForTrail(completedHike.hike.id)
  } else {
    console.error('Flyover modal launcher not found (showMapModalForTrail)')
  }
}

function exportGpx(completedHike) {
  // Not implemented for completed hikes - would need GPX endpoint
  console.log('Export GPX for:', completedHike)
}

function triggerCopyModel(event, completedHike) {
  if (shareModal.value && completedHike.hike?.shareableUrl) {
    const fullUrl = `${completedHike.hike.shareableUrl}`
    shareModal.value.show(fullUrl, completedHike.hike, 'hike')
  }
}

function launchAddToList(completedHike) {
  const hike = completedHike.hike

  if (window.onBoardingVue) {
    // Use the dedicated setAddToListContext method to set the hike ID
    if (typeof window.onBoardingVue.setAddToListContext === 'function' && hike?.id) {
      window.onBoardingVue.setAddToListContext({ hikeId: hike.id })
    }

    // Launch the add to list flow
    if (typeof window.onBoardingVue.launchFor === 'function') {
      window.onBoardingVue.launchFor(OnBoardingGoal.AddTrailToList)
    }
  }
}

function handleDownloadRequest(completedHike) {
  try {
    const hike = completedHike.hike
    if (!hike) return

    // Set global variables for download modal
    if (hike.mainCountry?.slug && hike.mainRegion?.slug && hike.slug) {
      window.country_slug = hike.mainCountry.slug
      window.region_slug = hike.mainRegion.slug
      window.slug = hike.slug
    }
    if (hike.id) {
      window.trail_id = hike.id
      window.trailId = hike.id
      window.hike_id = hike.id
      window.id = hike.id
    }
    if (hike.name) {
      window.trail_name = hike.name
      window.trailName = hike.name
    }
    if (hike.shareableUrl) {
      window.trail_share_url = hike.shareableUrl
      try {
        const url = new URL(hike.shareableUrl, window.location.origin)
        window.trail_download_path = url.pathname
      } catch (_) {
        window.trail_download_path = hike.shareableUrl
      }
    }

    if (window.openDownloadModal) {
      window.openDownloadModal()
    }
  } catch (error) {
    console.error('Error opening download modal:', error)
  }
}

onMounted(() => {
  fetchCompletedHikes(1)
})
</script>

<style scoped>
.completed-hikes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-completed-hikes {
  text-align: center;
  padding: 4rem 2rem;
}

.no-completed-hikes .material-icons {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
}

.see-more {
  margin-top: 1rem;
}

.see-more .btn-link {
  color: #FBCD01;
  text-decoration: none;
  font-weight: 600;
}

.see-more .btn-link:hover {
  text-decoration: underline;
}
</style>
